import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { switchMap } from 'rxjs/operators';

export interface SchemaResponse {
  schema_id: string;
  user_id: string;
  schema_name: string;
  schema_content: any;
  schema_config: SchemaConfig | null;
  table_id: string;
  created_at: number;
  updated_at: number;
}

export interface CreateSchemaDto {
  user_id: string;
  schema_content: any;
  table_id: string;
  schema_name: string;
  schema_config: SchemaConfig | null;
}

export interface SchemaConfig {
  configuration: SchemaConfiguration;
  [key: string]: unknown;
}

export interface SchemaConfiguration {
  require_provenance: boolean;
  fast_conversion: boolean;
  schema_splitting_threshold: number;
  multiple_rows: boolean;
  n_shot?: number;
  sheets: Record<string, SheetConfiguration>;
}

export interface SheetConfiguration {
  configuration: {
    multiple_rows: boolean;
  };
  fields: Record<string, SheetFieldConfig>;
}

export interface SheetFieldConfig {
  configuration: FieldConfiguration;
}

export interface FieldConfiguration {
  frequency: 'common' | 'occasional' | 'rare';
}
  
export interface UpdateSchemaDto {
  schema_content: any;
  schema_name?: string;
}

export interface ColumnDefinition {
  name: string;
  type: string;
  description?: string;
  example?: string;
  required?: boolean;
  options?: string[];
  presence?: 'occasional' | 'common' | 'rare';
}

export interface ReorderColumnsRequest {
  columns: ColumnDefinition[];
}

@Injectable({
  providedIn: 'root'
})
export class SchemaService {
  private readonly apiUrl = environment.backend_url + '/schema';

  constructor(private http: HttpClient) {}

  getAllSchemas(): Observable<SchemaResponse[]> {
    return this.http.get<SchemaResponse[]>(`${this.apiUrl}`);
  }

  getSchemaById(schemaId: string): Observable<SchemaResponse> {
    return this.http.get<SchemaResponse>(`${this.apiUrl}/${schemaId}`);
  }

  deleteSchema(schemaId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${schemaId}`);
  }

  updateSchema(schemaId: string, schema: UpdateSchemaDto): Observable<SchemaResponse> {
    return this.http.put<SchemaResponse>(`${this.apiUrl}/${schemaId}`, schema);
  }

  createSchema(schema: CreateSchemaDto): Observable<SchemaResponse> {
    return this.http.post<SchemaResponse>(`${this.apiUrl}`, schema);
  }

  // New methods for column operations
  addColumn(schemaId: string, sheetName: string, column: ColumnDefinition): Observable<SchemaResponse> {
    return this.getSchemaById(schemaId).pipe(
      switchMap(schema => {
        const updatedSchema = this.addColumnToSchema(schema.schema_content, sheetName, column);
        return this.updateSchema(schemaId, { schema_content: updatedSchema });
      })
    );
  }

  updateColumn(schemaId: string, sheetName: string, oldColumnName: string, column: ColumnDefinition): Observable<SchemaResponse> {
    return this.getSchemaById(schemaId).pipe(
      switchMap(schema => {
        const updatedSchema = this.updateColumnInSchema(schema.schema_content, sheetName, oldColumnName, column);
        return this.updateSchema(schemaId, { schema_content: updatedSchema });
      })
    );
  }

  deleteColumn(schemaId: string, sheetName: string, columnName: string): Observable<SchemaResponse> {
    return this.getSchemaById(schemaId).pipe(
      switchMap(schema => {
        const updatedSchema = this.deleteColumnFromSchema(schema.schema_content, sheetName, columnName);
        return this.updateSchema(schemaId, { schema_content: updatedSchema });
      })
    );
  }

  reorderColumns(schemaId: string, sheetName: string, columns: ColumnDefinition[]): Observable<SchemaResponse> {
    return this.getSchemaById(schemaId).pipe(
      switchMap(schema => {
        const updatedSchema = this.reorderColumnsInSchema(schema.schema_content, sheetName, columns);
        return this.updateSchema(schemaId, { schema_content: updatedSchema });
      })
    );
  }

  private addColumnToSchema(schema: any, sheetName: string, column: ColumnDefinition): any {
    const updatedSchema = { ...schema };
    let defName = sheetName.replace(/\s+/g, '');
    
    // If the expected defName doesn't exist, try to find it by looking at the sheets array
    if (!updatedSchema.$defs[defName]) {
      // Look through the sheets items to find the one with matching sheet_name
      if (updatedSchema.properties?.sheets?.items) {
        const sheetIndex = updatedSchema.properties.sheets.items.findIndex((item: any) => 
          item.properties?.sheet_name?.const === sheetName
        );
        
        if (sheetIndex !== -1) {
          // Try generic naming convention: Sheet_1, Sheet_2, etc.
          const genericDefName = `Sheet_${sheetIndex + 1}`;
          if (updatedSchema.$defs[genericDefName]) {
            defName = genericDefName;
          }
        }
      }
    }
    
    // Add column to $defs
    if (updatedSchema.$defs[defName]) {
      updatedSchema.$defs[defName].properties[column.name] = {
        type: column.type
      };
      if (column.options?.length) {
        updatedSchema.$defs[defName].properties[column.name].enum = column.options;
      }
      if (column.description) updatedSchema.$defs[defName].properties[column.name].description = column.description;
      if (column.example) updatedSchema.$defs[defName].properties[column.name].examples = [column.example];
      if (column.required) {
        if (!updatedSchema.$defs[defName].required) {
          updatedSchema.$defs[defName].required = [];
        }
        updatedSchema.$defs[defName].required.push(column.name);
      }
    } else {
      console.error(`Could not find $defs entry for sheet "${sheetName}" with defName "${defName}"`);
    }

    return updatedSchema;
  }

  private updateColumnInSchema(schema: any, sheetName: string, oldColumnName: string, column: ColumnDefinition): any {
    const updatedSchema = { ...schema };
    let defName = sheetName.replace(/\s+/g, '');
    
    // If the expected defName doesn't exist, try to find it by looking at the sheets array
    if (!updatedSchema.$defs[defName]) {
      // Look through the sheets items to find the one with matching sheet_name
      if (updatedSchema.properties?.sheets?.items) {
        const sheetIndex = updatedSchema.properties.sheets.items.findIndex((item: any) => 
          item.properties?.sheet_name?.const === sheetName
        );
        
        if (sheetIndex !== -1) {
          // Try generic naming convention: Sheet_1, Sheet_2, etc.
          const genericDefName = `Sheet_${sheetIndex + 1}`;
          if (updatedSchema.$defs[genericDefName]) {
            defName = genericDefName;
          }
        }
      }
    }
    
    if (updatedSchema.$defs[defName]) {
      const properties = updatedSchema.$defs[defName].properties;
      
      // If column name changed, we need to preserve order
      if (oldColumnName !== column.name) {
        // Get all property keys to preserve order
        const propertyKeys = Object.keys(properties);
        const oldIndex = propertyKeys.indexOf(oldColumnName);
        
        // Create new properties object with preserved order
        const newProperties: any = {};
        
        propertyKeys.forEach((key, index) => {
          if (key === oldColumnName) {
            // Replace old column with new column at same position
            newProperties[column.name] = {
              type: column.type
            };
            if (column.description) newProperties[column.name].description = column.description;
            if (column.example) newProperties[column.name].examples = [column.example];
            if (column.options?.length) newProperties[column.name].enum = column.options;
          } else {
            // Keep existing column (strip legacy presence)
            newProperties[key] = { ...properties[key] };
            delete newProperties[key].presence;
          }
        });
        
        updatedSchema.$defs[defName].properties = newProperties;
        
        // Update required array if column name changed
        if (updatedSchema.$defs[defName].required) {
          const requiredIndex = updatedSchema.$defs[defName].required.indexOf(oldColumnName);
          if (requiredIndex !== -1) {
            updatedSchema.$defs[defName].required[requiredIndex] = column.name;
          }
          if (column.required && !updatedSchema.$defs[defName].required.includes(column.name)) {
            updatedSchema.$defs[defName].required.push(column.name);
          } else if (!column.required && updatedSchema.$defs[defName].required.includes(column.name)) {
            updatedSchema.$defs[defName].required = updatedSchema.$defs[defName].required.filter((name: string) => name !== column.name);
          }
        } else if (column.required) {
          updatedSchema.$defs[defName].required = [column.name];
        }
      } else {
        // Column name didn't change, just update the properties in place
        updatedSchema.$defs[defName].properties[column.name] = {
          type: column.type
        };
        if (column.description) updatedSchema.$defs[defName].properties[column.name].description = column.description;
        if (column.example) {
          updatedSchema.$defs[defName].properties[column.name].examples = [column.example];
        } else {
          delete updatedSchema.$defs[defName].properties[column.name].examples;
        }
        if (column.options?.length) {
          updatedSchema.$defs[defName].properties[column.name].enum = column.options;
        } else {
          delete updatedSchema.$defs[defName].properties[column.name].enum;
        }
        delete updatedSchema.$defs[defName].properties[column.name].presence;

        // Update required status
        if (!updatedSchema.$defs[defName].required) {
          updatedSchema.$defs[defName].required = [];
        }
        
        const isCurrentlyRequired = updatedSchema.$defs[defName].required.includes(column.name);
        if (column.required && !isCurrentlyRequired) {
          updatedSchema.$defs[defName].required.push(column.name);
        } else if (!column.required && isCurrentlyRequired) {
          updatedSchema.$defs[defName].required = updatedSchema.$defs[defName].required.filter((name: string) => name !== column.name);
        }
      }
    } else {
      console.error(`Could not find $defs entry for sheet "${sheetName}" with defName "${defName}"`);
    }

    return updatedSchema;
  }

  private deleteColumnFromSchema(schema: any, sheetName: string, columnName: string): any {
    const updatedSchema = { ...schema };
    let defName = sheetName.replace(/\s+/g, '');
    
    // If the expected defName doesn't exist, try to find it by looking at the sheets array
    if (!updatedSchema.$defs[defName]) {
      // Look through the sheets items to find the one with matching sheet_name
      if (updatedSchema.properties?.sheets?.items) {
        const sheetIndex = updatedSchema.properties.sheets.items.findIndex((item: any) => 
          item.properties?.sheet_name?.const === sheetName
        );
        
        if (sheetIndex !== -1) {
          // Try generic naming convention: Sheet_1, Sheet_2, etc.
          const genericDefName = `Sheet_${sheetIndex + 1}`;
          if (updatedSchema.$defs[genericDefName]) {
            defName = genericDefName;
          }
        }
      }
    }
    
    if (updatedSchema.$defs[defName]) {
      // Remove column
      delete updatedSchema.$defs[defName].properties[columnName];
      if (updatedSchema.$defs[defName].required) {
        updatedSchema.$defs[defName].required = updatedSchema.$defs[defName].required.filter((name: string) => name !== columnName);
      }
    } else {
      console.error(`Could not find $defs entry for sheet "${sheetName}" with defName "${defName}"`);
    }

    return updatedSchema;
  }

  private reorderColumnsInSchema(schema: any, sheetName: string, columns: ColumnDefinition[]): any {
    const updatedSchema = { ...schema };
    
    // Find the correct $defs key for this sheet
    let defName = sheetName.replace(/\s+/g, '');
    
    // If the expected defName doesn't exist, try to find it by looking at the sheets array
    if (!updatedSchema.$defs[defName]) {
      // Look through the sheets items to find the one with matching sheet_name
      if (updatedSchema.properties?.sheets?.items) {
        const sheetIndex = updatedSchema.properties.sheets.items.findIndex((item: any) => 
          item.properties?.sheet_name?.const === sheetName
        );
        
        if (sheetIndex !== -1) {
          // Try generic naming convention: Sheet_1, Sheet_2, etc.
          const genericDefName = `Sheet_${sheetIndex + 1}`;
          if (updatedSchema.$defs[genericDefName]) {
            defName = genericDefName;
          }
        }
      }
      
      // If still not found, try to find any $defs key that might match
      if (!updatedSchema.$defs[defName]) {
        const defsKeys = Object.keys(updatedSchema.$defs || {});
        console.warn(`Could not find $defs key for sheet "${sheetName}". Available keys:`, defsKeys);
        
        // As a last resort, try the first available key if there's only one sheet
        if (defsKeys.length === 1) {
          defName = defsKeys[0];
          console.warn(`Using fallback $defs key: "${defName}"`);
        }
      }
    }
    
    if (updatedSchema.$defs[defName]) {
      // Create a new properties object with the reordered columns
      const newProperties: any = {};
      const requiredFields: string[] = [];

      columns.forEach(column => {
        // Copy the existing column definition
        const existingColumn = updatedSchema.$defs[defName].properties[column.name];
        if (existingColumn) {
          newProperties[column.name] = { ...existingColumn };
          
          // Preserve required status
          if (updatedSchema.$defs[defName].required?.includes(column.name)) {
            requiredFields.push(column.name);
          }
        }
      });

      // Update the schema with reordered columns
      updatedSchema.$defs[defName].properties = newProperties;
      updatedSchema.$defs[defName].required = requiredFields;
    } else {
      console.error(`Could not find $defs entry for sheet "${sheetName}" with defName "${defName}"`);
    }

    return updatedSchema;
  }
} 